import { Box } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CloudUploadSVG from "../../../../../../assets/images/icons/CloudUpload";
import BriaDropDown from "../../../../../../components/common/BriaDropDown/BriaDropDown";
import BriaSlider from "../../../../../../components/common/BriaSlider/BriaSlider";
import ConfigSwitch from "../../../../../../components/common/BriaSwitch/Config/ConfigSwitch";
import useStaticDropdown from "../../../../../../components/common/DropDowns/useStaticDropdown";
import ImageUploader from "../../../../../../components/common/ImageUploader/ImageUploader";
import { useAutoScrollToContent } from "../../../../../../hooks/useAutoScrollToContent";
import { useAppStore } from "../../../../../../hooks/useStores";
import InputLayout from "../../../../../../layout/InputLayout/InputLayout";
import { GuidanceMethodType, TextToImageConfigType } from "../../../../../../models/text-to-image";
import useImageUtils from "../../../../../../utils/useImageUtils";
import styles from "../../TextToImageConfig.module.scss";

interface ControlNetProps {
	controlNetNumber: 1 | 2;
	defaultEnabled?: boolean;
	scrollContainer?: React.RefObject<HTMLElement> | HTMLElement | null;
}

const TextToImageControlNet = ({ controlNetNumber, defaultEnabled = false, scrollContainer }: ControlNetProps) => {
	const { t } = useTranslation("image_generation", { keyPrefix: "playground.textToImage.config.controlNet" });
	const { textToImageStore } = useAppStore();
	const { handleConfigChange } = textToImageStore;
	const [isEnabled, setIsEnabled] = useState(defaultEnabled);
	const contentRef = useRef<HTMLDivElement>(null);
	const isInitialMount = useRef(true);
	const { getBase64 } = useImageUtils();

	const { scrollToRevealedContent } = useAutoScrollToContent({
		scrollContainer: scrollContainer || null,
		enabled: !!scrollContainer,
		offset: 20,
	});

	const { items: guidanceMethodOptions } = useStaticDropdown<GuidanceMethodType>({
		tKeyPrefix: "playground.textToImage.config.controlNet.guidanceMethod.options",
	});

	const guidanceMethodKey = `guidance_method_${controlNetNumber}` as keyof TextToImageConfigType;
	const guidanceScaleKey = `guidance_method_${controlNetNumber}_scale` as keyof TextToImageConfigType;
	const guidanceImageKey = `guidance_method_${controlNetNumber}_image_file` as keyof TextToImageConfigType;

	const guidanceMethod = textToImageStore.config[guidanceMethodKey] as GuidanceMethodType;
	const guidanceScale = textToImageStore.config[guidanceScaleKey] as number;
	const guidanceImage = textToImageStore.config[guidanceImageKey] as string;

	const handleControlNetToggle = (checked: boolean) => {
		setIsEnabled(checked);
		if (!checked) {
			delete textToImageStore.config[guidanceMethodKey];
			delete textToImageStore.config[guidanceScaleKey];
			delete textToImageStore.config[guidanceImageKey];
		}
	};

	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}

		if (isEnabled && scrollContainer && contentRef.current) {
			scrollToRevealedContent(null, contentRef.current);
		}
	}, [isEnabled]);

	const handleMethodChange = (e: any) => {
		handleConfigChange(guidanceMethodKey, e.target.value as GuidanceMethodType);
	};

	const handleScaleChange = (_event: any, scale: number | number[]) => {
		handleConfigChange(guidanceScaleKey, scale as number);
	};

	const handleImageUpload = async (e: any) => {
		const file = e.target.files?.[0];
		if (file) {
			const base64String = await getBase64(file);
			if (base64String) {
				handleConfigChange(guidanceImageKey, base64String as string);
			}
		}
	};

	const handleImageDelete = async () => {
		delete textToImageStore.config[guidanceImageKey];
	};

	return (
		<Box className={styles.inputs}>
			<ConfigSwitch
				label={`${t("label")}${controlNetNumber}`}
				info={t("info")}
				checked={isEnabled}
				onChange={handleControlNetToggle}
			/>

			{isEnabled && (
				<Box className={styles.inputs} ref={contentRef}>
					<InputLayout label={t("guidanceMethod.label")}>
						<BriaDropDown
							items={guidanceMethodOptions}
							value={guidanceMethod}
							onChange={handleMethodChange}
							placeholder={t("guidanceMethod.placeholder")}
						/>
					</InputLayout>

					<InputLayout label={t("guidanceImage.label")}>
						<ImageUploader
							description={t("guidanceImage.description")}
							icon={<CloudUploadSVG />}
							horizontalLayout
							largeIcon
							src={guidanceImage}
							onUpload={handleImageUpload}
							onDelete={handleImageDelete}
							emptyStateClassName={styles.imageUploader}
						/>
					</InputLayout>

					<InputLayout label={t("guidanceScale.label")} info={t("guidanceScale.info")}>
						<BriaSlider
							value={guidanceScale || 1}
							onChange={handleScaleChange}
							step={0.05}
							min={0}
							max={1}
							marks={[
								{ value: 0, label: "0" },
								{ value: 1, label: "1" },
							]}
							valueLabelDisplay="auto"
						/>
					</InputLayout>
				</Box>
			)}
		</Box>
	);
};

export default observer(TextToImageControlNet);
